import { type CreateSequenceBody, type EmailTemplateDTO, MAX_SEQUENCE_STEPS, MAX_STEP_DELAY_DAYS, type SequenceDetailDTO } from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, Field, IconButton, Input, MultiSelect, Select, Switch, Textarea } from '../../components/ui';
import { useLookups } from '../../hooks/useMeta';
import { api } from '../../lib/api';

export type SequenceFormState = {
  name: string;
  description: string;
  autoEnroll: boolean;
  requireApproval: boolean;
  relationshipKeys: string[];
  stageKeys: string[];
  minDaysSinceMeaningful: string;
  minDaysSinceLastContact: string;
  excludeOpenOpportunities: boolean;
  minScore: string;
  includeNeverContacted: boolean;
  reenrollCooldownDays: string;
  steps: { templateId: string; delayDays: string }[];
};

/** Defaults describe the most common sequence: a check-in with warm pizzerias that went quiet for a month. */
export const EMPTY_SEQUENCE: SequenceFormState = {
  name: '',
  description: '',
  autoEnroll: true,
  requireApproval: true,
  relationshipKeys: [],
  stageKeys: [],
  minDaysSinceMeaningful: '30',
  minDaysSinceLastContact: '14',
  excludeOpenOpportunities: true,
  minScore: '',
  includeNeverContacted: false,
  reenrollCooldownDays: '90',
  steps: [{ templateId: '', delayDays: '0' }],
};

const numberText = (value: number | null) => (value === null ? '' : String(value));
const optionalNumber = (value: string) => (value.trim() === '' ? null : Number(value));

export function formFromSequence(sequence: SequenceDetailDTO): SequenceFormState {
  return {
    name: sequence.name,
    description: sequence.description ?? '',
    autoEnroll: sequence.autoEnroll,
    requireApproval: sequence.requireApproval,
    relationshipKeys: sequence.relationshipKeys,
    stageKeys: sequence.stageKeys,
    minDaysSinceMeaningful: numberText(sequence.minDaysSinceMeaningful),
    minDaysSinceLastContact: numberText(sequence.minDaysSinceLastContact),
    excludeOpenOpportunities: sequence.excludeOpenOpportunities,
    minScore: numberText(sequence.minScore),
    includeNeverContacted: sequence.includeNeverContacted,
    reenrollCooldownDays: String(sequence.reenrollCooldownDays),
    steps: sequence.steps.map((step) => ({ templateId: step.templateId, delayDays: String(step.delayDays) })),
  };
}

export function bodyFromForm(form: SequenceFormState): Required<CreateSequenceBody> {
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    autoEnroll: form.autoEnroll,
    requireApproval: form.requireApproval,
    relationshipKeys: form.relationshipKeys,
    stageKeys: form.stageKeys,
    minDaysSinceMeaningful: optionalNumber(form.minDaysSinceMeaningful),
    minDaysSinceLastContact: optionalNumber(form.minDaysSinceLastContact),
    excludeOpenOpportunities: form.excludeOpenOpportunities,
    minScore: optionalNumber(form.minScore),
    includeNeverContacted: form.includeNeverContacted,
    reenrollCooldownDays: Number(form.reenrollCooldownDays || 0),
    steps: form.steps.map((step) => ({ templateId: step.templateId, delayDays: Number(step.delayDays || 0) })),
  };
}

export function SequenceForm({
  form,
  onChange,
  disabled,
}: {
  form: SequenceFormState;
  onChange: (form: SequenceFormState) => void;
  disabled: boolean;
}) {
  const { meta } = useLookups();
  const { data: templates } = useQuery({
    queryKey: ['templates', 'active'],
    queryFn: ({ signal }) => api.get<{ items: EmailTemplateDTO[] }>('/api/templates?active=true', { signal }),
  });
  const set = <K extends keyof SequenceFormState>(key: K, value: SequenceFormState[K]) => onChange({ ...form, [key]: value });
  const setStep = (index: number, patch: Partial<SequenceFormState['steps'][number]>) =>
    set('steps', form.steps.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  const moveStep = (index: number, by: -1 | 1) => {
    const steps = [...form.steps];
    const [moved] = steps.splice(index, 1);
    steps.splice(index + by, 0, moved!);
    set('steps', steps);
  };

  const relationshipOptions = (meta?.relationships ?? []).filter((r) => !r.blocksOutreach).map((r) => ({ value: r.key, label: r.label }));
  const stageOptions = (meta?.stages ?? []).filter((s) => s.kind === 'open').map((s) => ({ value: s.key, label: s.label }));

  return (
    <fieldset disabled={disabled} className="space-y-4">
      <Card>
        <CardHeader title="Sequence" />
        <CardBody className="space-y-3">
          <Field label="Name">
            <Input value={form.name} onChange={(event) => set('name', event.target.value)} placeholder="30-day check-in — warm pizzerias" />
          </Field>
          <Field label="Description">
            <Textarea rows={2} value={form.description} onChange={(event) => set('description', event.target.value)} />
          </Field>
          <Switch checked={form.autoEnroll} onChange={(value) => set('autoEnroll', value)} label="Enroll matching leads automatically" />
          <Switch checked={form.requireApproval} onChange={(value) => set('requireApproval', value)} label="Hold every email for approval before sending" />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Who gets it" description="Do-not-contact, closed, suppressed and unsubscribed pizzerias are always excluded." />
        <CardBody className="grid gap-3 sm:grid-cols-2">
          <Field label="Relationship" hint="None selected = any relationship that allows outreach.">
            <MultiSelect label="Relationship" options={relationshipOptions} value={form.relationshipKeys} onChange={(value) => set('relationshipKeys', value)} />
          </Field>
          <Field label="Pipeline stage" hint="None selected = any open stage.">
            <MultiSelect label="Stage" options={stageOptions} value={form.stageKeys} onChange={(value) => set('stageKeys', value)} />
          </Field>
          <Field label="No meaningful interaction for at least (days)">
            <Input type="number" min={0} value={form.minDaysSinceMeaningful} onChange={(event) => set('minDaysSinceMeaningful', event.target.value)} />
          </Field>
          <Field label="Not contacted for at least (days)">
            <Input type="number" min={0} value={form.minDaysSinceLastContact} onChange={(event) => set('minDaysSinceLastContact', event.target.value)} />
          </Field>
          <Field label="Minimum lead score" hint="Empty = any score.">
            <Input type="number" min={0} max={100} value={form.minScore} onChange={(event) => set('minScore', event.target.value)} />
          </Field>
          <Field label="Days before a lead can re-enter">
            <Input type="number" min={0} value={form.reenrollCooldownDays} onChange={(event) => set('reenrollCooldownDays', event.target.value)} />
          </Field>
          <Switch checked={form.excludeOpenOpportunities} onChange={(value) => set('excludeOpenOpportunities', value)} label="Skip pizzerias with an open opportunity" />
          <Switch checked={form.includeNeverContacted} onChange={(value) => set('includeNeverContacted', value)} label="Include pizzerias never contacted (intro sequence)" />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Steps" description="Delays count from enrollment (step 1) or from the previous step. A reply, call or meeting pauses the sequence." />
        <CardBody className="space-y-2">
          {form.steps.map((step, index) => (
            <div key={index} className="flex flex-wrap items-end gap-2 rounded-md border border-slate-200 p-2">
              <span className="w-12 pb-2 text-xs font-medium text-slate-500">Step {index + 1}</span>
              <Field label="Wait (days)" className="w-28">
                <Input type="number" min={0} max={MAX_STEP_DELAY_DAYS} value={step.delayDays} onChange={(event) => setStep(index, { delayDays: event.target.value })} />
              </Field>
              <Field label="Template" className="min-w-56 flex-1">
                <Select value={step.templateId} onChange={(event) => setStep(index, { templateId: event.target.value })} placeholder="Choose a template">
                  {(templates?.items ?? []).map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="flex gap-1 pb-1">
                <IconButton icon={ArrowUp} label="Move up" size="sm" disabled={index === 0} onClick={() => moveStep(index, -1)} />
                <IconButton icon={ArrowDown} label="Move down" size="sm" disabled={index === form.steps.length - 1} onClick={() => moveStep(index, 1)} />
                <IconButton icon={Trash2} label="Remove step" size="sm" disabled={form.steps.length === 1} onClick={() => set('steps', form.steps.filter((_, i) => i !== index))} />
              </div>
            </div>
          ))}
          <Button
            size="sm"
            icon={Plus}
            disabled={form.steps.length >= MAX_SEQUENCE_STEPS}
            onClick={() => set('steps', [...form.steps, { templateId: '', delayDays: '7' }])}
          >
            Add step
          </Button>
        </CardBody>
      </Card>
    </fieldset>
  );
}
